import React, { useState } from 'react';

const FAQS = [
  {
    q: 'What is Premsi?',
    a: 'Premsi is a social media platform that lets you sell digital products — videos, PDFs, exclusive content, and paid communities — directly to your fans in under 30 seconds, with instant delivery and daily payouts.',
  },
  {
    q: 'Is Premsi free to use?',
    a: 'Yes. Creating an account and starting to sell is completely free. Premsi takes a commission only when you make a sale — no monthly fees, no setup costs.',
  },
  {
    q: 'How much do I keep per sale?',
    a: 'You keep 85% of every sale. Premsi only earns when you do. Payouts can be requested daily through the app.',
  },
  {
    q: 'Do my fans need an account to buy from me?',
    a: 'No. Fans can purchase your premium content in under 30 seconds without creating an account. The content is delivered securely to their email.',
  },
  {
    q: 'What can I sell on Premsi?',
    a: 'You can sell premium photos, videos, PDFs, create paid or free subscription groups, and send premium messages. Courses and paid podcasts are coming soon.',
  },
  {
    q: 'How do I get paid?',
    a: 'You request payouts directly in the Premsi app. Payouts are processed daily — no waiting periods, no minimum thresholds.',
  },
  {
    q: 'What is Premsi+?',
    a: 'Premsi+ is a $3.49/month subscription for fans. It unlocks premium posts from every creator in one subscription — so fans don\'t need to pay each creator separately.',
  },
  {
    q: 'How do I grow my audience on Premsi?',
    a: 'Post free content to attract fans, then link it to your premium messages or groups. Share your Premsi profile on Instagram, TikTok, and X — your existing audience becomes paying customers.',
  },
    // ── AI features ──────────────────────────────────────────────────────────
    {
      q: 'What is Premsi AI?',
      a: 'Premsi AI is a built-in assistant that works inside your inbox. It replies to fans in your tone, recommends the right products to buy, and keeps conversations going — even when you are offline.',
    },
    {
      q: 'Can Premsi AI sell on my behalf while I sleep?',
      a: 'Yes. Premsi AI can handle fan messages, answer questions, and close sales 24/7. Every sale it closes is tracked in your dashboard with full details so you always stay in control.',
    },
    {
      q: 'Will the AI sound like me, or will fans notice it is automated?',
      a: 'Premsi AI is trained on your tone and communication style. Fans interact naturally — the AI keeps replies warm and personal, not robotic.',
    },
    {
      q: 'What is the AI Fulfillment Dashboard?',
      a: 'When Premsi AI closes a custom content request, it automatically creates a task card for you with the fan\'s preferences, payment status, and delivery deadline — so you never lose track of what you owe.',
    },
    {
      q: 'What is the AI Fan CRM?',
      a: 'The AI Fan CRM tracks each fan\'s spend, purchase history, and churn risk. It tells you who to message next and what to offer them — turning guesswork into targeted revenue.',
    },
    {
      q: 'What are Smart Upsells?',
      a: 'Smart Upsells let Premsi AI automatically suggest the right product — a bundle, premium message, tip, or membership — based on what each fan has already bought and what they are most likely to want next.',
    },
  
    // ── content & safety ─────────────────────────────────────────────────────
    {
      q: 'Is my content protected from leaks?',
      a: 'Yes. All premium content is watermarked and delivered through secure, access-controlled links. Fans cannot share or re-download content outside their purchase.',
    },
    {
      q: 'Can I control who sees my content?',
      a: 'Yes. You choose exactly what is free and what is paid. Premium content is locked until a fan purchases it — no account required on their end, but access is tied to their email.',
    },
  
    // ── referral & growth ────────────────────────────────────────────────────
    {
      q: 'How does the Premsi referral program work?',
      a: 'Refer another creator to Premsi and earn 5% of their earnings for life. There is no cap — the more creators you bring in, the more you earn passively alongside your own sales.',
    },
  
    // ── platform ─────────────────────────────────────────────────────────────
    {
      q: 'Is Premsi available as a mobile app?',
      a: 'Yes. Premsi is available on iOS and Android. You can manage your store, reply to fans, track sales, and request payouts entirely from your phone.',
    },
    {
      q: 'What kinds of creators use Premsi?',
      a: 'Premsi is built for any creator with an audience — fitness coaches, musicians, influencers, adult content creators, educators, photographers, and more. If you have fans, Premsi helps you monetize them.',
    },
];

const FAQ_INITIAL_VISIBLE = 7;

export default function AltFAQ({
  eyebrow        = 'FAQ',
  headline       = 'Common questions.',
  faqs           = FAQS,
  initialVisible = FAQ_INITIAL_VISIBLE,
}) {
  const [openIndex, setOpenIndex] = useState(null);
  const [showAll, setShowAll] = useState(false);

  const visibleFaqs = showAll ? faqs : faqs.slice(0, initialVisible);
  const hasMore = faqs.length > initialVisible;

  const toggle = (i) => setOpenIndex(prev => (prev === i ? null : i));

  const handleToggleVisibility = () => {
    if (showAll) setOpenIndex(null);
    setShowAll(prev => !prev);
  };

  return (
    <section className="alt-faq" aria-labelledby="faq-heading">
      <div className="alt-faq__inner alt-container">
        <div className="alt-faq__header">
          <p  className="alt-faq__eyebrow">{eyebrow}</p>
          <h2 id="faq-heading" className="alt-faq__headline">{headline}</h2>
        </div>

        <div className="alt-faq__content">
          <dl className="alt-faq__list">
            {visibleFaqs.map(({ q, a }, i) => {
              const isOpen = openIndex === i;
              return (
                <div key={q} className={`alt-faq__item${isOpen ? ' alt-faq__item--open' : ''}`}>
                  <dt>
                    <button
                      type="button"
                      className="alt-faq__question"
                      onClick={() => toggle(i)}
                      aria-expanded={isOpen}
                      aria-controls={`faq-answer-${i}`}
                      id={`faq-question-${i}`}
                    >
                      <span>{q}</span>
                      <span className="alt-faq__chevron" aria-hidden="true">
                        {isOpen ? '−' : '+'}
                      </span>
                    </button>
                  </dt>
                  <dd
                    id={`faq-answer-${i}`}
                    role="region"
                    aria-labelledby={`faq-question-${i}`}
                    className="alt-faq__answer"
                    hidden={!isOpen}
                  >
                    <p>{a}</p>
                  </dd>
                </div>
              );
            })}
          </dl>

          {hasMore && (
            <button
              type="button"
              className="alt-faq__toggle"
              onClick={handleToggleVisibility}
              aria-expanded={showAll}
            >
              {showAll ? 'Show less' : 'Read more answers'}
            </button>
          )}
        </div>
      </div>
    </section>
  );
}
